import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  X,
  Heart,
  Share2,
  ShoppingCart,
  Check,
  CheckCircle,
  Play,
  Calendar,
  ChevronRight,
  ShieldCheck,
  Clock,
  ExternalLink,
  ArrowLeft,
  Ticket,
  Info,
  Download,
  Lock,
  Sparkles
} from 'lucide-react';
import {
  fetchProducts,
  selectAllProducts,
  setSelectedCategory,
  fetchStoreDiscounts,
  selectStoreDiscounts
} from '../store/slices/productsSlice';
import { calculateDiscountedPrice } from '../utils/priceUtils';
import {
  addToCart,
  updateCartItemValidity,
  setCartOpen,
  selectCartItems,
  fetchCoupons
} from '../store/slices/cartSlice';
import { toggleWishlist } from '../store/slices/wishlistSlice';
import { database } from '../services/firebase';
import { ref, get } from 'firebase/database';
import { formatPrice, getScriptTypeLabels, resolveScriptDownloadItems, calculateRemainingDays, formatDurationLabel, resolvePriceForValidity, sortDurationKeys, getDurationDays, durationKeyFromDays, resolveAddonPriceForBaseValidity, isCouponVisibleToUser, isItemEligibleForCoupon } from '../utils/helpers';
import toastManager from '../utils/toastManager';
import { useTranslation } from 'react-i18next';
import ExtensionModal from '../components/modals/ExtensionModal';
import { subscribeTestedSites, getTestedSitesForProduct, getDomainFavicon } from '../services/testedSitesService';
import ShareModal from '../components/modals/ShareModal';
import ProductCard from '../components/product/ProductCard';
import CouponCard from '../components/store/CouponCard';
import {
  isExternalProduct,
  normalizeCategory,
  isFreeScriptProduct,
  isPurchasableProduct,
  isWishlistEligibleProduct,
  resolveFreeScriptDownloadLink
} from '../utils/productRules';
import { isExtensionDiscountEligibleAccess } from '../utils/extensionEligibility';

// --- Sub-components for Cleaner File ---

const ValidityPill = ({ label, isSelected, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-w-[86px] rounded-2xl border px-4 py-2.5 text-sm font-black transition-all ${isSelected
        ? 'border-gray-950 bg-gray-950 text-white shadow-sm dark:border-white dark:bg-white dark:text-gray-950'
        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-white/[0.055] dark:text-gray-300 dark:hover:bg-white/[0.09]'
        }`}
    >
      {formatDurationLabel(label)}
    </button>
  );
};

const InfoSection = ({ title, children, icon: Icon }) => {
  return (
    <section className="border-b border-gray-100 py-4 last:border-0 dark:border-white/10">
      <div className="mb-3 flex items-center gap-2.5">
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-white/[0.07] dark:text-gray-300">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <span className="text-sm font-black text-gray-950 dark:text-white">
          {title}
        </span>
      </div>
      <div className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {children}
      </div>
    </section>
  );
};

// --- Main Component ---

const ProductDetail = () => {
  const { t } = useTranslation();
  const id = useParams().id;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux Selectors
  const allProducts = useSelector(selectAllProducts);
  const {
    premium: premiumList = [],
    exclusive: exclusiveList = [],
    bundle: bundleList = [],
    freeScripts: freeList = [],
    others: otherList = []
  } = useSelector((s) => s.products || {});
  const wishlistItems = useSelector(state => state.wishlist?.items || []);
  const cartItems = useSelector(selectCartItems);
  const storeDiscounts = useSelector(selectStoreDiscounts);
  const { coupons = [] } = useSelector((state) => state.cart || {});

  // Settings for deep link
  const settingsSelector = useSelector((state) => state.settings.settings);
  const universalLink = settingsSelector?.universalScriptLink || '';
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Find Product
  const lookupProducts = useMemo(
    () => (
      Array.isArray(allProducts) && allProducts.length
        ? allProducts
        : [...premiumList, ...exclusiveList, ...bundleList, ...freeList, ...otherList]
    ),
    [allProducts, premiumList, exclusiveList, bundleList, freeList, otherList]
  );

  const lookupProductIndex = useMemo(
    () => Object.fromEntries(
      lookupProducts
        .filter((item) => item?.id)
        .map((item) => [String(item.id), item])
    ),
    [lookupProducts]
  );

  const product = useMemo(
    () => lookupProducts.find((p) => p.id === id) || null,
    [lookupProducts, id]
  );

  // Derived State
  const isWishlisted = wishlistItems.some(item => item.id === product?.id);
  const displayName = product?.name || product?.Name || 'Product';
  const displayImage = product?.image || product?.['icon link'] || '';
  const isExternal = isExternalProduct(product);
  const isFreeScript = isFreeScriptProduct(product);
  const isPurchasable = isPurchasableProduct(product);
  const wishlistEnabled = isWishlistEligibleProduct(product);
  const externalLink = product?.ExternalLink || product?.externalLink;
  const freeDownloadLink = resolveFreeScriptDownloadLink(product, settingsSelector || {});
  const isBundle = String(product?.category || '').toLowerCase() === 'bundle';
  const pkgName = product?.packageName || product?.EmbeddedScript?.packageName || null;
  const scriptTypeLabels = useMemo(() => getScriptTypeLabels(product), [product]);
  const downloadCatalog = useMemo(() => resolveScriptDownloadItems(settingsSelector || {}), [settingsSelector]);
  const normalizedProductCategory = normalizeCategory(product?.category);
  const linkedCounterpart = useMemo(() => {
    if (!product?.linkedProductId || lookupProducts.length === 0) return null;
    const match = lookupProducts.find((item) => item.id === product.linkedProductId);
    if (!match) return null;

    const counterpartCategory = normalizeCategory(match.category);
    if (normalizedProductCategory === 'free') {
      return ['premium', 'exclusive'].includes(counterpartCategory) ? match : null;
    }
    if (normalizedProductCategory === 'premium' || normalizedProductCategory === 'exclusive') {
      return counterpartCategory === 'free' ? match : null;
    }
    return null;
  }, [lookupProducts, product?.linkedProductId, normalizedProductCategory]);

  // State
  const [selectedValidity, setSelectedValidity] = useState(null);
  const [selectedAddonValidity, setSelectedAddonValidity] = useState({});
  const [userOrders, setUserOrders] = useState([]);
  const [ownedAccessRecord, setOwnedAccessRecord] = useState(null);
  const [userAccessMap, setUserAccessMap] = useState({});
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [showAddonSelectionModal, setShowAddonSelectionModal] = useState(false);
  const [selectedPromptAddonIds, setSelectedPromptAddonIds] = useState([]);
  const [activeBundleTile, setActiveBundleTile] = useState(null); // New state for tile interaction
  const [allTestedSites, setAllTestedSites] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribeTestedSites((sites) => {
      setAllTestedSites(sites);
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const productTestedSites = useMemo(
    () => getTestedSitesForProduct(allTestedSites, product, lookupProductIndex),
    [allTestedSites, product, lookupProductIndex]
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const [isScriptInfoOpen, setIsScriptInfoOpen] = useState(false);
  const scriptInfoRef = useRef(null);

  // Price Map Logic
  const priceMap = useMemo(() => product?.pricingRaw || product?.Price || {}, [product]);
  const validityOptions = useMemo(() => {
    if (!isPurchasable) {
      return [];
    }
    if (isExternal) {
      const nonZeroPrices = Object.entries(priceMap).filter(([, v]) => typeof v === 'number' && v > 0);
      const sortedExternalKeys = sortDurationKeys(nonZeroPrices.map(([key]) => key));
      return sortedExternalKeys.length > 0 ? [sortedExternalKeys[0]] : [];
    }
    return priceMap && typeof priceMap === 'object'
      ? sortDurationKeys(Object.keys(priceMap).filter((k) => typeof priceMap[k] === 'number' && priceMap[k] > 0))
      : [];
  }, [priceMap, isExternal, isPurchasable]);

  useEffect(() => {
    if (validityOptions.length > 0) setSelectedValidity(validityOptions[0]);
    else setSelectedValidity(null);
  }, [id, validityOptions]);

  const activeAddons = useMemo(() => (
    Array.isArray(product?.addons)
      ? product.addons
          .filter((addon) => String(addon?.status || 'active').toLowerCase() === 'active')
          .sort((left, right) => (Number(left.sortOrder || 0) - Number(right.sortOrder || 0)) || String(left.name || '').localeCompare(String(right.name || '')))
      : []
  ), [product?.addons]);

  useEffect(() => {
    const defaults = {};
    activeAddons.forEach((addon) => {
      const keys = sortDurationKeys(Object.keys(addon.pricingRaw || {}).filter((key) => Number(addon.pricingRaw?.[key]) > 0));
      if (keys[0]) defaults[addon.id] = keys[0];
    });
    setSelectedAddonValidity(defaults);
  }, [activeAddons]);

  useEffect(() => {
    if (!isScriptInfoOpen) return undefined;
    const handleOutsidePress = (event) => {
      if (scriptInfoRef.current && !scriptInfoRef.current.contains(event.target)) {
        setIsScriptInfoOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsScriptInfoOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsidePress);
    document.addEventListener('touchstart', handleOutsidePress);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePress);
      document.removeEventListener('touchstart', handleOutsidePress);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isScriptInfoOpen]);

  // Fetch Products if missing
  useEffect(() => {
    if (!Array.isArray(allProducts) || allProducts.length === 0) {
      dispatch(fetchProducts());
    }
    dispatch(fetchStoreDiscounts());
    if (!coupons?.length) {
      dispatch(fetchCoupons());
    }
  }, [allProducts, dispatch, coupons?.length]);

  // Check Orders
  useEffect(() => {
    if (user?.uid && product?.id) {
      const loadUserOrders = async () => {
        try {
          const accessSnapshot = await get(ref(database, `Users/${user.uid}/ProductAccess`));
          const accessMap = accessSnapshot.exists() ? (accessSnapshot.val() || {}) : {};
          setUserAccessMap(accessMap);
          setOwnedAccessRecord(accessMap?.[product.id] || null);

          const ordersSnapshot = await get(ref(database, `Users/${user.uid}/Orders`));
          if (ordersSnapshot.exists()) {
            const userOrdersObj = ordersSnapshot.val() || {};
            const orderMap = new Map();
            const legacyIds = [];

            Object.entries(userOrdersObj).forEach(([orderId, value]) => {
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                orderMap.set(orderId, value);
              } else {
                legacyIds.push(orderId);
              }
            });

            if (legacyIds.length > 0) {
              const fetchPromises = legacyIds.map(async (id) => {
                try {
                  const snap = await get(ref(database, `Order index/${id}`));
                  if (snap.exists()) return { id, data: snap.val() };
                } catch (e) {}
                return null;
              });
              const results = await Promise.all(fetchPromises);
              results.forEach(res => {
                if (res?.data && typeof res.data === 'object' && res.data.Customer === user.uid) {
                  orderMap.set(res.id, res.data);
                }
              });
            }

            const userOrdersList = Array.from(orderMap.entries()).map(([id, data]) => ({ id, ...data }));
            setUserOrders(userOrdersList);
          } else {
            setUserOrders([]);
          }
        } catch (error) {
          setOwnedAccessRecord(null);
          setUserAccessMap({});
        }
      };
      loadUserOrders();
    } else {
      setOwnedAccessRecord(null);
      setUserAccessMap({});
      setUserOrders([]);
    }
  }, [user?.uid, product?.id]);

  const buildAccessInfo = (accessRecord = {}) => {
    const status = String(accessRecord.status || 'active').toLowerCase();
    const isLifetime = accessRecord.expiresAt === null || accessRecord.expiresAt === 'lifetime' || (!accessRecord.expiresAt && String(accessRecord.validity || '').toLowerCase() === 'lifetime');
    const expiresAt = accessRecord.expiresAt ? new Date(accessRecord.expiresAt) : null;
    const isExpired = !isLifetime && expiresAt && expiresAt.getTime() <= Date.now();
    const remainingDays = !isLifetime && expiresAt
      ? Math.max(0, Math.round(((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) * 10) / 10)
      : null;

    return {
      status,
      isLifetime,
      isExpired: Boolean(isExpired || status === 'expired'),
      remainingDays,
      orderId: accessRecord.orderId || '',
      validity: accessRecord.validity || '',
      price: null
    };
  };

  const bundleAccessRecord = useMemo(() => {
    if (!product?.id || !userAccessMap || typeof userAccessMap !== 'object') return null;
    for (const [accessProductId, accessRecord] of Object.entries(userAccessMap)) {
      if (!accessRecord || typeof accessRecord !== 'object') continue;
      const candidate = lookupProductIndex[String(accessProductId)] || lookupProducts.find((item) => item.id === accessProductId);
      if (normalizeCategory(candidate?.category) !== 'bundle') continue;
      const bundleProducts = Array.isArray(candidate?.Products) ? candidate.Products : candidate?.bundleItems || [];
      if (!bundleProducts.map(String).includes(String(product.id))) continue;
      const info = buildAccessInfo(accessRecord);
      if (info.status !== 'revoked' && info.status !== 'inactive' && !info.isExpired) {
        return { ...accessRecord, inheritedFromBundle: accessProductId };
      }
    }
    return null;
  }, [lookupProductIndex, lookupProducts, product?.id, userAccessMap]);

  const ownedAccessInfo = useMemo(() => {
    if (!isAuthenticated || !product?.id) {
      return { hasPurchased: false, isExpired: false, orderId: '', validity: '', remainingDays: null, price: null, source: '', extensionDiscountEligible: false };
    }

    if (ownedAccessRecord) {
      const info = buildAccessInfo(ownedAccessRecord);

      if (info.status !== 'revoked' && info.status !== 'inactive') {
        return {
          hasPurchased: true,
          isExpired: info.isExpired,
          orderId: info.orderId,
          validity: info.validity,
          remainingDays: info.remainingDays,
          price: null,
          source: 'direct',
          extensionDiscountEligible: isExtensionDiscountEligibleAccess({
            productId: product.id,
            accessRecord: ownedAccessRecord,
            orders: userOrders
          })
        };
      }
    }

    if (bundleAccessRecord) {
      const info = buildAccessInfo(bundleAccessRecord);
      return {
        hasPurchased: true,
        isExpired: info.isExpired,
        orderId: info.orderId,
        validity: info.validity,
        remainingDays: info.remainingDays,
        price: null,
        source: 'bundle',
        inheritedFromBundle: bundleAccessRecord.inheritedFromBundle,
        extensionDiscountEligible: false
      };
    }

    if (!userOrders.length) {
      return { hasPurchased: false, isExpired: false, orderId: '', validity: '', remainingDays: null, price: null, source: '', extensionDiscountEligible: false };
    }

    let fallbackMatch = null;
    for (const order of userOrders) {
      const isApproved = ['approved', 'delivered'].includes(String(order.Status || order.status || '').toLowerCase());
      if (!isApproved) continue;
      const items = Array.isArray(order.Items) ? order.Items : Object.values(order.Items || {});
      const item = items.find(item => {
        if (!item) return false;
        if (item.itemType === 'addon' || item.addonId || item.addonFor) return false;
        const pid = item.productId || item.ProductId || item.id;
        return pid === product?.id;
      });
      if (!item) continue;

      const orderId = order.id || '';
      const validity = item.validity || '';
      const price = Number(item.price ?? item.Price ?? 0) || null;
      const approvalDate = order.ApprovedAt || order.approvedAt || order['Order date'] || order.createdAt || '';
      if (!validity || String(validity).toLowerCase() === 'lifetime') {
        fallbackMatch = fallbackMatch || { hasPurchased: true, isExpired: false, orderId, validity, remainingDays: null, price, source: 'order', extensionDiscountEligible: true };
        continue;
      }

      const remainingDays = calculateRemainingDays(approvalDate, validity, Date.now());
      const isExpired = Number.isFinite(Number(remainingDays)) && Number(remainingDays) <= 0;
      if (!isExpired) return { hasPurchased: true, isExpired: false, orderId, validity, remainingDays, price, source: 'order', extensionDiscountEligible: true };
      fallbackMatch = fallbackMatch || { hasPurchased: true, isExpired: true, orderId, validity, remainingDays, price, source: 'order', extensionDiscountEligible: true };
    }

    return fallbackMatch || { hasPurchased: false, isExpired: false, orderId: '', validity: '', remainingDays: null, price: null, source: '', extensionDiscountEligible: false };
  }, [isAuthenticated, ownedAccessRecord, bundleAccessRecord, userOrders, product?.id]);
  const hasPurchasedProduct = ownedAccessInfo.hasPurchased;
  const purchaseExtensionMode = ownedAccessInfo.isExpired ? 'renew' : 'extend';

  const ownedProductDownloadItems = useMemo(() => {
    const hasLua = Boolean(product?.scriptFormats?.lua);
    const hasJson = Boolean(product?.scriptFormats?.json);
    if (!hasPurchasedProduct || (!hasLua && !hasJson)) return [];

    const merged = [];
    const seen = new Set();
    const addItems = (items = [], scriptType = '') => {
      items.forEach((item, index) => {
        if (!item?.link) return;
        const key = `${item.link}|${scriptType}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push({
          id: `${scriptType}-${index}-${item.link}`,
          name: item.name || 'Download File',
          description: item.description || '',
          link: item.link,
          scriptType,
        });
      });
    };

    if (hasLua) addItems(downloadCatalog.luaItems, 'lua');
    if (hasJson) addItems(downloadCatalog.jsonItems, 'json');
    return merged;
  }, [product, hasPurchasedProduct, downloadCatalog]);

  // Helpers
  const isPrivate = String(product?.status || '').toLowerCase() === 'private';
  const isActive = (!product?.status || product.status === 'Active' || product.status === 'active') && !isPrivate;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  const getPrice = () => {
    let basePrice = 0;
    const matchedPrice = resolvePriceForValidity(priceMap, selectedValidity);
    if (matchedPrice !== null) {
      basePrice = matchedPrice;
    } else if (typeof product?.price === 'number') {
      basePrice = product.price;
    } else {
      const nums = Object.values(priceMap || {}).filter((v) => typeof v === 'number');
      basePrice = nums.length ? Math.min(...nums) : 0;
    }
    return basePrice;
  };

  const currentBasePrice = getPrice();
  const { finalPrice, isDiscounted, discountPercentage } = calculateDiscountedPrice(product, currentBasePrice, selectedValidity, storeDiscounts);

  const buildProductForCart = () => ({
    id: product.id,
    name: displayName,
    price: isDiscounted ? finalPrice : currentBasePrice,
    image: displayImage,
    category: product.category,
    originalPrice: isDiscounted ? currentBasePrice : null,
    storeDiscount: isDiscounted ? discountPercentage : null,
  });

  const isInCart = useMemo(() => {
    if (!product?.id) return false;
    return cartItems.some(item => item.id === product.id && item.validity === selectedValidity);
  }, [cartItems, product?.id, selectedValidity]);

  const isBaseProductInCart = useMemo(() => (
    product?.id ? cartItems.some((item) => item.id === product.id) : false
  ), [cartItems, product?.id]);

  const baseProductCartItem = useMemo(() => (
    product?.id ? cartItems.find((item) => item.id === product.id) || null : null
  ), [cartItems, product?.id]);
  const isSwappableCartValidity = Boolean(
    baseProductCartItem &&
    selectedValidity &&
    (baseProductCartItem.validity || null) !== (selectedValidity || null)
  );

  const buildRelatedAddonUpdates = (nextValidity) => {
    if (!product?.id || !nextValidity) return [];
    return cartItems
      .filter((item) => item.addonFor === product.id && item.addonId)
      .map((item) => {
        const addon = activeAddons.find((candidate) => String(candidate.id) === String(item.addonId));
        if (!addon) return null;
        const adjusted = resolveAddonPriceForBaseValidity(addon.pricingRaw || {}, priceMap, nextValidity);
        if (adjusted.price === null || adjusted.price < 0) return null;
        return {
          id: item.id,
          fromValidity: item.validity || null,
          item: {
            ...item,
            price: adjusted.price,
            validity: nextValidity,
            addonAdjustedDays: adjusted.days || null,
            addonBaseValidity: nextValidity,
            addonPricingSource: 'cart_main'
          }
        };
      })
      .filter(Boolean);
  };

  const swapBaseProductInCart = () => {
    if (!baseProductCartItem || !selectedValidity) return false;
    const productForCart = buildProductForCart();
    dispatch(updateCartItemValidity({
      id: product.id,
      fromValidity: baseProductCartItem.validity || null,
      item: {
        ...productForCart,
        validity: selectedValidity
      },
      relatedItems: buildRelatedAddonUpdates(selectedValidity)
    }));
    return true;
  };

  const getAddonPurchaseContext = (addon) => {
    const pricingRaw = addon?.pricingRaw || {};
    if (hasPurchasedProduct && !ownedAccessInfo.isExpired) {
      if (Number.isFinite(Number(ownedAccessInfo.remainingDays)) && Number(ownedAccessInfo.remainingDays) > 0) {
        const adjustedDays = Number(ownedAccessInfo.remainingDays);
        const validity = durationKeyFromDays(adjustedDays);
        const adjusted = resolveAddonPriceForBaseValidity(pricingRaw, priceMap, validity);
        return {
          validity,
          price: adjusted.price,
          adjustedDays,
          isAdjusted: Boolean(adjusted.isProrated),
          label: `${formatDurationLabel(validity)} remaining`,
          source: ownedAccessInfo.source === 'bundle' ? 'bundle_remaining' : 'owned_remaining',
          hint: ownedAccessInfo.source === 'bundle'
            ? t('product.addon_bundle_remaining_hint', 'Priced for your bundle remaining validity.')
            : t('product.addon_remaining_hint', 'Priced for your current remaining validity.')
        };
      }

      const lifetimePrice = resolvePriceForValidity(pricingRaw, 'lifetime');
      const fallbackValidity = lifetimePrice !== null ? 'lifetime' : selectedAddonValidity[addon.id] || sortDurationKeys(Object.keys(pricingRaw || {}))[0] || null;
      const fallbackPrice = resolvePriceForValidity(pricingRaw, fallbackValidity);
      return {
        validity: fallbackValidity,
        price: fallbackPrice,
        adjustedDays: getDurationDays(fallbackValidity),
        isAdjusted: false,
        label: fallbackValidity ? formatDurationLabel(fallbackValidity) : '',
        source: ownedAccessInfo.source === 'bundle' ? 'bundle_owned' : 'owned',
        hint: ownedAccessInfo.source === 'bundle'
          ? t('product.addon_bundle_access_hint', 'Available because this script is included in your bundle.')
          : t('product.addon_owned_access_hint', 'Available for your active product access.')
      };
    }

    const baseValidity = selectedValidity || baseProductCartItem?.validity;
    const adjusted = resolveAddonPriceForBaseValidity(pricingRaw, priceMap, baseValidity);
    return {
      validity: baseValidity,
      price: adjusted.price,
      adjustedDays: adjusted.days,
      isAdjusted: Boolean(adjusted.isProrated),
      label: baseValidity ? formatDurationLabel(baseValidity) : '',
      source: baseProductCartItem ? 'cart_main' : 'new_main',
      hint: baseProductCartItem
        ? t('product.addon_cart_validity_hint', 'Matches the main product already in your cart.')
        : t('product.addon_main_validity_hint', 'Matches the main product validity you selected.')
    };
  };

  const addonAccessMap = useMemo(() => {
    if (ownedAccessRecord?.addons && typeof ownedAccessRecord.addons === 'object') {
      return ownedAccessRecord.addons;
    }
    if (bundleAccessRecord?.addons && typeof bundleAccessRecord.addons === 'object') {
      return bundleAccessRecord.addons;
    }
    return {};
  }, [bundleAccessRecord, ownedAccessRecord]);

  const isAddonOwned = (addonId) => {
    const access = addonAccessMap?.[addonId];
    if (!access) return false;
    const status = String(access.status || 'active').toLowerCase();
    if (status === 'revoked' || status === 'inactive') return false;
    if (access.expiresAt === null || access.expiresAt === 'lifetime') return true;
    const expiresAt = access.expiresAt ? new Date(access.expiresAt).getTime() : 0;
    return !expiresAt || expiresAt > Date.now();
  };

  const isAddonInCart = (addonId) => cartItems.some((item) =>
    item.addonFor === product?.id &&
    item.addonId === addonId
  );

  const getAddableAddonOptions = () => activeAddons
    .map((addon) => {
      const addonContext = getAddonPurchaseContext(addon);
      const owned = isAddonOwned(addon.id);
      const inCart = isAddonInCart(addon.id);
      const baseRequired = addon.requiresBaseProduct !== false;
      const baseMissing = baseRequired && !hasPurchasedProduct && !isBaseProductInCart && !selectedValidity;
      const canBuy = !owned && !inCart && !baseMissing && addonContext.price !== null && addonContext.price >= 0;
      return { addon, addonContext, owned, inCart, canBuy };
    })
    .filter((option) => option.canBuy);

  const addonPromptOptions = getAddableAddonOptions();

  const availableCoupons = useMemo(() => {
    if (!coupons || !product || !isPurchasable) return [];
    
    // We only show active, unexpired coupons
    const activeCoupons = coupons.filter(c => 
      (!c.status || c.status.toLowerCase() === 'active') && 
      (!c.endDate || new Date(c.endDate) > new Date())
    );

    const productCouponContext = {
      id: product.id,
      productId: product.id,
      category: product.category,
      validity: selectedValidity
    };

    return activeCoupons.filter(coupon => (
      isCouponVisibleToUser(coupon, user) &&
      isItemEligibleForCoupon(productCouponContext, coupon)
    )).sort((a, b) => {
      // Sort so that fully eligible ones come first
      const aEligible = currentBasePrice >= (Number(a.minPurchaseAmount) || 0);
      const bEligible = currentBasePrice >= (Number(b.minPurchaseAmount) || 0);
      if (aEligible && !bEligible) return -1;
      if (!aEligible && bEligible) return 1;
      return 0; // maintain original order otherwise
    });
  }, [coupons, product, selectedValidity, isPurchasable, currentBasePrice, user]);

  // Actions
  const addAddonToCartWithContext = (addon, addonContext) => {
    if (!addon?.id || !addonContext) return false;
    const validity = addonContext.validity || null;
    const price = addonContext.price;
    if (price === null || price < 0) return false;

    dispatch(addToCart({
      product: {
        id: `${product.id}::addon::${addon.id}`,
        name: addon.name,
        price,
        image: addon.image || displayImage,
        category: 'addon',
        addonFor: product.id,
        addonId: addon.id,
        addonName: addon.name,
        baseProductName: displayName,
        addonPricingSource: addonContext.source,
        addonAdjustedDays: addonContext.adjustedDays || null,
        addonBaseValidity: addonContext.validity || selectedValidity || baseProductCartItem?.validity || ownedAccessInfo.validity || null
      },
      validity
    }));
    return true;
  };

  const addProductAndSelectedAddonsToCart = (addonIds = []) => {
    if (isSwappableCartValidity) {
      swapBaseProductInCart();
    } else if (!isBaseProductInCart) {
      const productForCart = buildProductForCart();
      dispatch(addToCart({ product: productForCart, validity: selectedValidity }));
    }

    const selectedAddonIdSet = new Set(addonIds.map(String));
    const selectedAddonOptions = getAddableAddonOptions().filter((option) => selectedAddonIdSet.has(String(option.addon.id)));
    let addedAddonCount = 0;
    selectedAddonOptions.forEach(({ addon, addonContext }) => {
      if (addAddonToCartWithContext(addon, addonContext)) {
        addedAddonCount += 1;
      }
    });

    setShowAddonSelectionModal(false);
    setSelectedPromptAddonIds([]);
    toastManager.success(
      addedAddonCount > 0
        ? t('cart.added_with_addons', { name: displayName, count: addedAddonCount, defaultValue: `${displayName} added with ${addedAddonCount} addon${addedAddonCount === 1 ? '' : 's'}` })
        : t('cart.added_to_cart', { name: displayName })
    );
  };

  const handleAddToCart = () => {
    if (!isPurchasable) {
      toastManager.error(t('product.not_for_sale', 'This product is not for sale'));
      return;
    }

    if (isInCart) {
      dispatch(setCartOpen(true));
      return;
    }
    if (isSwappableCartValidity && swapBaseProductInCart()) {
      toastManager.success(t('cart.validity_updated', 'Plan updated'));
      return;
    }
    const addableAddons = getAddableAddonOptions();
    if (addableAddons.length > 0) {
      setSelectedPromptAddonIds(addableAddons.map((option) => option.addon.id));
      setShowAddonSelectionModal(true);
      return;
    }
    addProductAndSelectedAddonsToCart([]);
  };

  const ensureBaseProductInCartForAddon = () => {
    if (hasPurchasedProduct) return true;
    if (isBaseProductInCart) {
      if (isSwappableCartValidity) {
        swapBaseProductInCart();
      }
      return true;
    }
    if (!isPurchasable || !selectedValidity) return false;

    const productForCart = buildProductForCart();
    dispatch(addToCart({ product: productForCart, validity: selectedValidity }));
    return true;
  };

  const handleAddAddonToCart = (addon) => {
    if (!addon?.id || !product?.id) return;
    if (addon.requiresBaseProduct !== false && !ensureBaseProductInCartForAddon()) {
      toastManager.error(t('product.add_base_first', 'Select the main product validity before buying this addon.'));
      return;
    }

    const addonContext = getAddonPurchaseContext(addon);
    if (addonContext.price === null || addonContext.price < 0) {
      toastManager.error(t('product.addon_price_missing', 'This addon does not have a valid price for the selected validity.'));
      return;
    }

    if (isAddonInCart(addon.id)) {
      dispatch(setCartOpen(true));
      return;
    }

    if (addAddonToCartWithContext(addon, addonContext)) {
      toastManager.success(t('cart.added_to_cart', { name: addon.name }));
    }
  };

  const handleToggleWishlist = (e) => {
    e.stopPropagation();
    if (!product) return;
    if (!wishlistEnabled && !isWishlisted) {
      toastManager.error(t('product.not_wishlistable', 'This product cannot be added to wishlist'));
      return;
    }
    dispatch(toggleWishlist(product));
    toastManager.success(isWishlisted ? t('product.removed_from_wishlist') : t('product.added_to_wishlist'));
  };

  const generateShareLink = () => {
    if (universalLink && universalLink.startsWith('https://t.me/')) {
      const separator = universalLink.includes('?') ? '&' : '?';
      return `${universalLink}${separator}startapp=product_${id}`;
    }
    return `https://t.me/Psh_store_bot/app?startapp=product_${id}`;
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleBackToProducts = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      dispatch(setSelectedCategory('all'));
      navigate('/products'); // Fallback if opened directly
    }
  };

  const renderPrimaryAction = ({ compact = false } = {}) => {
    const sizeClass = compact ? 'h-12 rounded-2xl text-sm' : 'h-14 rounded-2xl text-base';
    const baseClass = `inline-flex w-full items-center justify-center gap-2 font-black transition-all active:scale-[0.98] ${sizeClass}`;
    const disabledClass = `${baseClass} border border-dashed border-gray-300 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-500`;
    const primaryClass = `${baseClass} bg-gray-950 text-white shadow-lg shadow-gray-950/10 hover:-translate-y-0.5 hover:bg-gray-800 dark:bg-white dark:text-gray-950 dark:shadow-none dark:hover:bg-gray-100`;

    // Users who have access can manage and use the product regardless of catalog status
    // BUT no renew or buy if disabled or private
    if (hasPurchasedProduct) {
      if (!isPurchasable) {
        return (
          <div className={disabledClass}>
            <CheckCircle className="h-4 w-4 text-emerald-500" />
            {ownedAccessInfo.isExpired
              ? t('orders.expired', 'Expired')
              : `${t('common.owned', 'Owned')} (${ownedAccessInfo.isLifetime ? t('orders.lifetime', 'Lifetime') : `${ownedAccessInfo.remainingDays ?? 0}d`})`}
          </div>
        );
      }

      return (
        <button
          type="button"
          onClick={() => setShowExtensionModal({
            productId: product.id,
            productName: displayName,
            basePrice: getPrice(),
            originalOrderId: ownedAccessInfo.orderId,
            mode: purchaseExtensionMode,
            currentValidity: ownedAccessInfo.validity || '',
            originalPaidPrice: ownedAccessInfo.price,
            remainingDays: ownedAccessInfo.remainingDays,
            extensionDiscountEligible: ownedAccessInfo.extensionDiscountEligible,
            accessSource: ownedAccessInfo.source,
            addons: activeAddons,
            addonAccessMap
          })}
          className={primaryClass}
        >
          <Sparkles className="h-4 w-4" />
          {purchaseExtensionMode === 'renew' ? t('orders.renew', 'Renew') : t('orders.extend', 'Extend')}
        </button>
      );
    }

    if (!isPurchasable || !isActive) {
      return (
        <div className={disabledClass}>
          <Lock className="h-4 w-4" />
          {t('product.unavailable', 'Currently Unavailable')}
        </div>
      );
    }

    if (isFreeScript) {
      return (
        <button
          type="button"
          onClick={() => freeDownloadLink && window.open(freeDownloadLink, '_blank')}
          disabled={!freeDownloadLink}
          className={freeDownloadLink ? primaryClass : disabledClass}
        >
          <ExternalLink className="h-4 w-4" />
          {freeDownloadLink ? t('product.download_now', 'Download Script') : t('product.download_unavailable', 'Download unavailable')}
        </button>
      );
    }

    if (isExternal) {
      return (
        <button
          type="button"
          onClick={() => window.open(externalLink, '_blank')}
          className={primaryClass}
        >
          <ExternalLink className="h-4 w-4" />
          {t('product.visit_website', 'Visit Website')}
        </button>
      );
    }

    if (!isPurchasable) {
      return (
        <div className={disabledClass}>
          {t('product.not_for_sale', 'Not for sale')}
        </div>
      );
    }

    if (hasPurchasedProduct) {
      return (
        <button
          type="button"
          onClick={() => setShowExtensionModal({
            productId: product.id,
            productName: displayName,
            basePrice: getPrice(),
            originalOrderId: ownedAccessInfo.orderId,
            mode: purchaseExtensionMode,
            currentValidity: ownedAccessInfo.validity || '',
            originalPaidPrice: ownedAccessInfo.price,
            remainingDays: ownedAccessInfo.remainingDays,
            extensionDiscountEligible: ownedAccessInfo.extensionDiscountEligible,
            accessSource: ownedAccessInfo.source,
            addons: activeAddons,
            addonAccessMap
          })}
          className={`${baseClass} bg-blue-600 text-white shadow-lg shadow-blue-600/15 hover:-translate-y-0.5 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600`}
        >
          <Calendar className="h-5 w-5" />
          {purchaseExtensionMode === 'renew' ? t('orders.renew', 'Renew') : t('orders.extend', compact ? 'Extend' : 'Extend Access')}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleAddToCart}
        className={`${baseClass} ${isInCart
          ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/15 hover:bg-emerald-700'
          : 'bg-primary-600 text-white shadow-lg shadow-primary-600/15 hover:-translate-y-0.5 hover:bg-primary-700 dark:bg-white dark:text-gray-950 dark:shadow-none dark:hover:bg-gray-100'
        }`}
      >
        <ShoppingCart className="h-5 w-5" />
        {isInCart
          ? t('product.view_cart')
          : isSwappableCartValidity
            ? t('cart.swap_to_validity', { validity: formatDurationLabel(selectedValidity), defaultValue: `Swap to ${formatDurationLabel(selectedValidity)}` })
            : t('product.add_to_cart')}
      </button>
    );
  };

  if (!product) return <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-500 dark:bg-gray-950 dark:text-gray-400">Loading...</div>;

  if (isPrivate && !hasPurchasedProduct) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 text-center dark:bg-gray-950">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-white/[0.05] dark:text-gray-500">
          <Lock className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-black text-gray-950 dark:text-white">
          {t("product.private_unavailable_title", "Product Unavailable")}
        </h2>
        <p className="mt-2 max-w-sm text-sm text-gray-500 dark:text-gray-400">
          {t("product.private_unavailable_desc", "This product is private and not available on the public store.")}
        </p>
        <button
          type="button"
          onClick={handleBackToProducts}
          className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-primary-600 px-6 py-3 text-sm font-black text-white shadow-lg transition-all hover:bg-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("product.back_to_catalog", "Back to Catalog")}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28 text-gray-950 dark:bg-gray-950 dark:text-white md:pb-10">

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ delay: 0.2 }}
        className="container mx-auto hidden max-w-7xl items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 md:flex"
      >
        <button onClick={handleBackToProducts} className="flex items-center gap-1 rounded-full px-2 py-1 transition-colors hover:bg-white hover:text-gray-950 dark:hover:bg-white/[0.07] dark:hover:text-white">
          <ArrowLeft size={16} /> {t('product.back')}
        </button>
        <span className="text-gray-300 dark:text-gray-700">/</span>
        <span className="capitalize">{product.category}</span>
        <span className="text-gray-300 dark:text-gray-700">/</span>
        <span className="max-w-md truncate font-black text-gray-950 dark:text-white notranslate" translate="no">{displayName}</span>
      </motion.div>

      <div className="mx-auto max-w-7xl px-0 md:px-6">
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_440px] xl:grid-cols-[minmax(0,1fr)_480px]">

          {/* LEFT: Image Section */}
          <div className="relative px-3 md:sticky md:top-24 md:px-0">
            <motion.div
              className="relative flex aspect-[1/1] w-full items-center justify-center overflow-hidden rounded-[2rem] border border-gray-200/70 bg-white p-1.5 shadow-sm dark:border-white/10 dark:bg-white/[0.045] md:aspect-[4/3] md:p-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Mobile Back Button Overlay */}
              <button
                onClick={handleBackToProducts}
                className="absolute left-4 top-4 z-10 rounded-2xl border border-white/60 bg-white/85 p-2 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-gray-950/70 md:hidden"
              >
                <ArrowLeft size={20} className="text-gray-900 dark:text-white" />
              </button>
              {/* Wishlist Button Overlay (Mobile Only) */}
              {wishlistEnabled && (
                <button
                  onClick={handleToggleWishlist}
                  className="absolute right-14 top-4 z-10 rounded-2xl border border-white/60 bg-white/85 p-2.5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-gray-950/70 md:hidden"
                >
                  <Heart size={20} className={isWishlisted ? "fill-red-500 text-red-500" : "text-gray-600 dark:text-gray-400"} />
                </button>
              )}
              {/* Share Button Overlay (Mobile Only) */}
              <button
                onClick={handleShare}
                className="absolute right-4 top-4 z-10 rounded-2xl border border-white/60 bg-white/85 p-2.5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-gray-950/70 md:hidden"
              >
                <Share2 size={20} className="text-gray-600 dark:text-gray-400" />
              </button>

              <motion.img
                src={displayImage}
                alt={displayName}
                className="h-full w-full rounded-[1.55rem] object-cover md:rounded-[1.35rem]"
              />

              {/* Badges */}
              <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                {product.category === 'bundle' && (
                  <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-black text-gray-950 shadow-sm backdrop-blur dark:bg-gray-950/80 dark:text-white">
                    {t('common.bundle')}
                  </span>
                )}
                <span className="flex items-center gap-1 rounded-full border border-white/25 bg-black/55 px-3 py-1 text-xs font-black uppercase tracking-wide text-white shadow-sm backdrop-blur-md">
                  {product.category}
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-wide shadow-sm backdrop-blur-md ${isPrivate ? 'bg-purple-600 text-white' : isActive ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                  {isPrivate ? '🔒 Private' : isActive ? 'Active' : t('product.unavailable', 'Unavailable')}
                </span>
              </div>
            </motion.div>
          </div>

          {/* RIGHT: Product Info & Actions - Slide Up */}
          <motion.div
            className="space-y-4 px-3 py-4 md:px-0 md:py-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{ delay: 0.1 }}
          >

            <section className="rounded-[2rem] border border-gray-200/70 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.045] sm:p-5">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-gray-600 dark:bg-white/[0.07] dark:text-gray-300">
                        {product.category}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        {t('product.verified', 'Verified Working')}
                      </span>
                    </div>
                    <h1 className="text-3xl font-black leading-[0.98] tracking-normal text-gray-950 dark:text-white md:text-4xl notranslate" translate="no">
                  {displayName}
                    </h1>
                  </div>
                  <div className="hidden shrink-0 items-center gap-2 md:flex">
                    {wishlistEnabled && (
                      <button
                        type="button"
                        onClick={handleToggleWishlist}
                        className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition-all ${isWishlisted
                          ? 'border-red-200 bg-red-50 text-red-500 dark:border-red-500/30 dark:bg-red-500/10'
                          : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:text-gray-950 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-300 dark:hover:bg-white/[0.1] dark:hover:text-white'}`}
                        aria-label={t('product.wishlist', 'Wishlist')}
                      >
                        <Heart size={20} className={isWishlisted ? 'fill-current' : ''} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleShare}
                      className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-gray-500 transition-all hover:border-gray-300 hover:text-gray-950 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-300 dark:hover:bg-white/[0.1] dark:hover:text-white"
                      aria-label={t('common.share', 'Share')}
                    >
                      <Share2 size={20} />
                    </button>
                  </div>
                </div>

              {scriptTypeLabels.length > 0 && (
                <div className="rounded-2xl border border-sky-200/70 bg-sky-50/70 shadow-sm dark:border-sky-400/20 dark:bg-sky-400/10">
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-black uppercase tracking-[0.12em] text-sky-700 dark:text-sky-200">
                        {t('product.script_type', 'Script Type')}
                      </div>
                      <div className="relative" ref={scriptInfoRef}>
                        <button
                          type="button"
                          onClick={() => setIsScriptInfoOpen((prev) => !prev)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-xl border border-sky-200 bg-white text-sky-700 transition-all hover:scale-105 dark:border-sky-400/20 dark:bg-gray-950/70 dark:text-sky-200"
                          aria-label={t('product.script_type_info_aria', 'Script type information')}
                          aria-expanded={isScriptInfoOpen}
                          aria-haspopup="dialog"
                        >
                          <Info size={14} />
                        </button>
                        {isScriptInfoOpen && (
                          <div className="absolute right-0 top-9 z-30 w-[280px] rounded-2xl border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-xl dark:border-white/10 dark:bg-gray-900 dark:text-gray-200">
                            <p>
                              <span className="font-semibold text-gray-900 dark:text-white">{t('product.game_guardian_script', 'Game Guardian Script')}:</span>{' '}
                              {t('product.game_guardian_script_desc', 'Works using the Game Guardian app (PSH GG).')}
                            </p>
                            <p className="mt-2">
                              <span className="font-semibold text-gray-900 dark:text-white">{t('product.mod_menu_script', 'Mod Menu Script')}:</span>{' '}
                              {t('product.mod_menu_script_desc', 'Works using Our Exclusive RuntimeX App / Js Hook / Terminal.')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {scriptTypeLabels.map((label) => (
                        <span
                          key={label}
                            className="inline-flex items-center rounded-full border border-sky-200/80 bg-white/90 px-3 py-1.5 text-xs font-black text-gray-900 dark:border-sky-400/20 dark:bg-gray-950/70 dark:text-gray-100"
                        >
                          {t(`product.${label}`, label)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {pkgName && (
                <div className="pt-1">
                  <a 
                    href={`https://play.google.com/store/apps/details?id=${pkgName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-black text-primary-700 transition-all hover:bg-white dark:border-white/10 dark:bg-white/[0.06] dark:text-primary-200 dark:hover:bg-white/[0.1]"
                  >
                    <ExternalLink size={16} />
                    {t('product.view_on_play_store', 'View on Play Store')}
                  </a>
                </div>
              )}
              </div>
            </section>

            {linkedCounterpart && (
              <div className="rounded-[1.5rem] border border-indigo-200 bg-indigo-50/80 p-4 dark:border-indigo-400/20 dark:bg-indigo-400/10">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-300">
                      {normalizedProductCategory === 'free' ? 'Paid Version Available' : 'Free Version Available'}
                    </div>
                    <div className="mt-1 text-base font-bold text-gray-900 dark:text-white notranslate" translate="no">
                      {linkedCounterpart.name || linkedCounterpart.Name}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {normalizedProductCategory === 'free'
                        ? 'Want the full paid release? Open the linked paid version.'
                        : 'Want to try it first? Open the linked free version.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/product/${linkedCounterpart.id}`)}
                    className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 transition-colors"
                  >
                    {normalizedProductCategory === 'free' ? 'View Paid Version' : 'View Free Version'}
                  </button>
                </div>
              </div>
            )}

            {/* Pricing Block */}
            <section className="rounded-[2rem] border border-gray-200/70 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.045] sm:p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    {isFreeScript ? t('product.access_type', 'Access') : t('product.total_price')}
                  </p>
                  <div className="mt-1">
                    {isFreeScript ? (
                      <span className="text-3xl font-black uppercase text-emerald-600 dark:text-emerald-300">
                        {t('product.free', 'Free')}
                      </span>
                    ) : (
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="text-3xl font-black text-gray-950 dark:text-white notranslate" translate="no">
                          {formatPrice(isDiscounted ? finalPrice : currentBasePrice)}
                        </span>
                        {isDiscounted && (
                          <span className="text-base font-bold text-gray-400 line-through notranslate" translate="no">
                            {formatPrice(currentBasePrice)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1">
                  {!isFreeScript && (isDiscounted || product.discount) ? (
                    <span className="rounded-full bg-red-500 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white">
                      {isDiscounted ? `${discountPercentage}% ${t('product.off')}` : `-${product.discount}%`}
                    </span>
                  ) : !isExternal && !isFreeScript ? (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200">
                      {t('product.best_price')}
                    </span>
                  ) : null}
                  {(() => {
                    if (!isBundle || !Array.isArray(product?.Products) || isFreeScript) return null;
                    const totalValue = product.Products.filter(Boolean).reduce((sum, pid) => {
                      const p = lookupProductIndex[String(pid)];
                      return sum + (p?.price || 0);
                    }, 0);
                    if (totalValue <= currentBasePrice) return null;
                    return (
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-black text-gray-500 line-through dark:bg-white/[0.07] dark:text-gray-400 notranslate" translate="no">
                        {t('product.value_label', 'Value')}: {formatPrice(totalValue)}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </section>

            <section className="rounded-[2rem] border border-gray-200/70 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.045] sm:p-5">
              {isPurchasable && !isExternal && validityOptions.length > 0 && (
                <div>
                  <span className="text-[11px] font-black uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">{t('product.select_validity')}</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {validityOptions.map(v => (
                      <ValidityPill
                        key={v}
                        label={v}
                        isSelected={selectedValidity === v}
                        onClick={() => setSelectedValidity(v)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className={`${isPurchasable && !isExternal && validityOptions.length > 0 ? 'mt-4 border-t border-gray-100 pt-4 dark:border-white/10' : ''} hidden md:block`}>
                {renderPrimaryAction()}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-white/10 dark:bg-white/[0.04]">
                  <ShieldCheck className="mb-2 h-5 w-5 text-emerald-500" />
                  <span className="block text-xs font-black text-gray-950 dark:text-white">{t('product.secure')}</span>
                  <span className="mt-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">{t('product.secure_subtitle')}</span>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-white/10 dark:bg-white/[0.04]">
                  <Clock className="mb-2 h-5 w-5 text-sky-500" />
                  <span className="block text-xs font-black text-gray-950 dark:text-white">{t('product.fast_delivery')}</span>
                  <span className="mt-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">{t('product.fast_delivery_subtitle')}</span>
                </div>
              </div>
            </section>

            {activeAddons.length > 0 && isPurchasable && !isExternal && !isFreeScript && (
              <section className="rounded-[2rem] border border-violet-200/70 bg-violet-50/55 p-4 shadow-sm dark:border-violet-400/20 dark:bg-violet-400/10 sm:p-5">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-violet-700 dark:text-violet-200">
                      <Sparkles className="h-4 w-4" />
                      {t('product.addon_features', 'Addon features')}
                    </div>
                    <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('product.addon_features_subtitle', 'Optional feature blocks delivered inside this script.')}
                    </p>
                  </div>
                  {activeAddons.length > 0 && (
                    <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-violet-700 shadow-sm dark:bg-gray-950/60 dark:text-violet-200">
                      {activeAddons.length}
                    </span>
                  )}
                </div>

                <div className="space-y-2.5">
                  {activeAddons.map((addon) => {
                    const addonContext = getAddonPurchaseContext(addon);
                    const selectedAddonPlan = addonContext.validity;
                    const addonPrice = addonContext.price;
                    const owned = isAddonOwned(addon.id);
                    const inCart = isAddonInCart(addon.id);
                    const baseRequired = addon.requiresBaseProduct !== false;
                    const baseMissing = baseRequired && !hasPurchasedProduct && !isBaseProductInCart && !selectedValidity;
                    const canBuyAddon = isPurchasable && !owned && !baseMissing && addonPrice !== null && addonPrice >= 0;

                    return (
                      <div key={addon.id} className="rounded-2xl border border-white/80 bg-white/90 p-3 shadow-sm dark:border-white/10 dark:bg-gray-950/45">
                        <div className="flex items-start gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-violet-100 text-violet-700 dark:bg-violet-400/10 dark:text-violet-200">
                            {addon.image ? (
                              <img src={addon.image} alt={addon.name} className="h-full w-full object-cover" />
                            ) : (
                              <Sparkles className="h-5 w-5" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-sm font-black text-gray-950 dark:text-white notranslate" translate="no">
                                {addon.name}
                              </h3>
                              {owned && (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200">
                                  {t('common.owned', 'Owned')}
                                </span>
                              )}
                              {inCart && !owned && (
                                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-black text-primary-700 dark:bg-primary-400/10 dark:text-primary-200">
                                  {t('cart.in_cart', 'In cart')}
                                </span>
                              )}
                            </div>
                            {addon.description && (
                              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                                {addon.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {!owned && selectedAddonPlan && (
                          <div className="mt-3 rounded-xl border border-violet-100 bg-violet-50/70 px-3 py-2 text-xs text-violet-800 dark:border-violet-400/20 dark:bg-violet-400/10 dark:text-violet-100">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-black">{addonContext.label}</span>
                              {addonContext.isAdjusted && (
                                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-violet-700 dark:bg-gray-950/70 dark:text-violet-200">
                                  {t('product.adjusted_price', 'Adjusted')}
                                </span>
                              )}
                            </div>
                            {addonContext.hint && (
                              <p className="mt-1 text-[11px] text-violet-700/80 dark:text-violet-200/80">{addonContext.hint}</p>
                            )}
                          </div>
                        )}

                        <div className="mt-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            {baseMissing ? (
                              <p className="text-[11px] font-bold text-amber-700 dark:text-amber-300">
                                {t('product.select_base_validity_first', 'Select main validity first')}
                              </p>
                            ) : addonPrice !== null && addonPrice >= 0 && !owned ? (
                              <p className="text-sm font-black text-gray-950 dark:text-white notranslate" translate="no">
                                {addonPrice > 0 ? formatPrice(addonPrice) : t('product.free', 'Free')}
                                {selectedAddonPlan && (
                                  <span className="ml-1 text-[11px] font-bold text-gray-500 dark:text-gray-400">
                                    /{formatDurationLabel(selectedAddonPlan)}
                                  </span>
                                )}
                              </p>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => (inCart ? dispatch(setCartOpen(true)) : handleAddAddonToCart(addon))}
                            disabled={owned || (!inCart && !canBuyAddon)}
                            className={`inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-xs font-black transition-all ${owned
                              ? 'cursor-default bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200'
                              : inCart
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : canBuyAddon
                                  ? 'bg-gray-950 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100'
                                  : 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-white/[0.06] dark:text-gray-500'
                            }`}
                          >
                            {owned ? (
                              <>
                                <Check className="h-4 w-4" />
                                {t('common.owned', 'Owned')}
                              </>
                            ) : inCart ? (
                              <>
                                <ShoppingCart className="h-4 w-4" />
                                {t('product.view_cart', 'View cart')}
                              </>
                            ) : (
                              <>
                                <ShoppingCart className="h-4 w-4" />
                                {hasPurchasedProduct || isBaseProductInCart ? t('product.add_addon', 'Add addon') : t('product.add_with_main', 'Add with main')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {hasPurchasedProduct && ownedProductDownloadItems.length > 0 && (
              <div className="rounded-[2rem] border border-blue-200 bg-blue-50/80 p-4 shadow-sm dark:border-blue-400/20 dark:bg-blue-400/10 sm:p-5">
                <div className="flex items-center gap-2 text-sm font-black text-blue-800 dark:text-blue-200">
                  <Download className="w-4 h-4" />
                  {t('downloads.files_title', 'Download Files')}
                </div>
                {ownedProductDownloadItems.some((item) => item.scriptType === 'lua') && (
                  <p className="mt-2 text-[11px] text-gray-700 dark:text-gray-300">
                    {t('downloads.gg_hint', 'Download GG files if you want to use Game Guardian.')}
                  </p>
                )}
                {ownedProductDownloadItems.some((item) => item.scriptType === 'json') && (
                  <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
                    {t('downloads.mod_hint', 'Download Mod Menu files if you want to use RuntimeX / Js Hook.')}
                  </p>
                )}
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {ownedProductDownloadItems.map((downloadItem) => (
                    <a
                      key={downloadItem.id}
                      href={downloadItem.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-between gap-3 rounded-2xl border border-blue-200/80 bg-white/90 px-3 py-2.5 transition-colors hover:bg-blue-100/70 dark:border-blue-400/20 dark:bg-gray-950/70 dark:hover:bg-blue-400/10"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {downloadItem.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-primary-600 dark:text-primary-400 mt-0.5">
                          {downloadItem.scriptType === 'lua' ? t('downloads.lua_label', 'LUA / Game Guardian') : t('downloads.json_label', 'JSON / Mod Menu')}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-primary-600 dark:text-primary-400 shrink-0">
                        <Download className="w-4 h-4" />
                        <ExternalLink className="w-3.5 h-3.5" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}


            {/* Info Sections (Tutorial, Description, Features, etc.) */}
            <section className="rounded-[2rem] border border-gray-200/70 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.045] sm:p-5">

              {/* Tutorial Section - MOVED TO TOP */}
              {(() => {
                const tutorialUrl = product?.tutorial || product?.tutorialVideoLink || product?.['Tutorial Video Link'];
                if (!isBundle && tutorialUrl) {
                  return (
                    <InfoSection title={t('product.video_tutorial')} icon={Play}>
                      <div className="mt-1">
                        <p className="mb-3 text-xs text-gray-500">{t('product.learn_to_use')}</p>
                        <a
                          href={tutorialUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 p-3 font-black text-red-600 transition-colors hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/15"
                          onClick={(e) => {
                            if (!tutorialUrl.startsWith('http')) {
                              e.preventDefault();
                              window.open(`https://${tutorialUrl}`, '_blank');
                            }
                          }}
                        >
                          <Play size={16} fill="currentColor" />
                          {t('product.watch_tutorial')}
                        </a>
                      </div>
                    </InfoSection>
                  );
                }
              })()}

              <InfoSection title={t('product.description')}>
                {product?.description || t('product.no_description')}
              </InfoSection>

              {/* Bundle Products List */}
              {isBundle && (
                <InfoSection title={t('product.bundle_contents')}>
                  <div className="mt-2 space-y-3">
                    {/* Logic to find included products from 'Products' array of IDs */}
                    {(() => {
                      const includedIds = Array.isArray(product?.Products) ? product.Products.filter(Boolean) : [];
                      const includedProducts = includedIds.map((pid) => lookupProductIndex[String(pid)]).filter(Boolean);

                      if (includedProducts.length === 0) return <p>No products linked.</p>;

                      // Tile Grid for many items
                      if (includedProducts.length > 5) {
                        return (
                          <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6">
                            {includedProducts.map((bp, idx) => {
                              const isActive = activeBundleTile === (bp.id || idx);
                              return (
                                <div
                                  key={bp.id || idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveBundleTile(isActive ? null : (bp.id || idx));
                                  }}
                                  className={`relative aspect-square cursor-pointer overflow-hidden rounded-2xl border transition-all ${isActive
                                    ? 'border-transparent ring-2 ring-gray-950 dark:ring-white'
                                    : 'border-gray-100 bg-gray-50 hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.055] dark:hover:border-white/20'
                                    }`}
                                >
                                  <img
                                    src={bp.image || bp['icon link'] || '/icon.png'}
                                    alt={bp.name}
                                    className="w-full h-full object-contain"
                                  />

                                  {/* Overlay logic: Only show when Active */}
                                  {isActive && (
                                    <div className="absolute inset-0 z-10 bg-black/80 backdrop-blur-[2px] flex flex-col items-center justify-center p-1 text-center animate-in fade-in duration-200">
                                      <p className="text-[9px] text-white font-medium line-clamp-2 mb-1.5 leading-tight px-1">
                                        {bp.name || bp.Name}
                                      </p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/product/${bp.id}`);
                                        }}
                                        className="px-2 py-0.5 bg-white text-black text-[9px] font-bold rounded-full hover:bg-gray-200 transition-colors shadow-sm"
                                      >
                                        Open
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      // Standard List for fewer items
                      return includedProducts.map((bp, idx) => (
                        <div
                          key={bp.id || idx}
                          onClick={() => navigate(`/product/${bp.id}`)}
                          className="flex cursor-pointer items-center gap-3 rounded-2xl border border-gray-100 p-2 transition-colors hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.06]"
                        >
                          <img
                            src={bp.image || bp['icon link'] || '/icon.png'}
                            alt={bp.name}
                            className="h-12 w-12 rounded-2xl bg-gray-100 object-cover"
                          />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">{bp.name || bp.Name}</h4>
                            <span className="text-xs text-gray-500 notranslate" translate="no">{typeof bp.price === 'number' ? formatPrice(bp.price) : bp.price}</span>
                          </div>
                          <ChevronRight size={16} className="text-gray-400" />
                        </div>
                      ));
                    })()}
                  </div>
                </InfoSection>
              )}


              {!isBundle && (
                <InfoSection title={t('product.key_features')} icon={Check}>
                  <ul className="mt-2 grid grid-cols-1 gap-2">
                    {product.features && product.features.length > 0 ? (
                      product.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <Check size={14} className="text-green-500" /> {feature}
                        </li>
                      ))
                    ) : (
                      <>
                        <li className="flex items-center gap-2"><Check size={14} className="text-green-500" /> {t('product.high_quality')}</li>
                        <li className="flex items-center gap-2"><Check size={14} className="text-green-500" /> {t('product.instant_access')}</li>
                        <li className="flex items-center gap-2"><Check size={14} className="text-green-500" /> {t('product.verified_working')}</li>
                      </>
                    )}
                  </ul>
                </InfoSection>
              )}

              {/* TESTED SITES SECTION (SEPARATE PER PRODUCT, AGGREGATE FOR BUNDLES) */}
              {!isBundle ? (
                <InfoSection title={t('product.tested_sites_title', 'Tested Sites & Telemetry')} icon={ShieldCheck}>
                  <div className="mt-2 space-y-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {t('product.tested_sites_desc', 'Verified platforms where tracking and telemetry scripts have been tested specifically for this tool.')}
                    </p>

                    {productTestedSites.length > 0 ? (
                      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {productTestedSites.map((site) => (
                          <div
                            key={site.id}
                            className="flex items-start justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3 transition-colors hover:border-gray-200 dark:border-white/10 dark:bg-white/[0.035]"
                          >
                            <div className="flex items-start gap-2.5 min-w-0">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200/80 bg-white p-1 shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
                                <img
                                  src={site.iconUrl || getDomainFavicon(site.url)}
                                  alt=""
                                  className="h-full w-full object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-xs text-gray-950 dark:text-white truncate">
                                    {site.name}
                                  </span>
                                  <span className={`inline-flex items-center px-1.5 py-0.2 rounded-md text-[10px] font-bold ${
                                    site.status === 'working'
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                                      : 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300'
                                  }`}>
                                    {site.status === 'working' ? 'Verified' : 'Testing'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                  {site.domain}
                                </p>
                                {site.notes && (
                                  <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2">
                                    {site.notes}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                              {site.accuracyRate && (
                                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400">
                                  {site.accuracyRate}
                                </span>
                              )}
                              <a
                                href={site.url?.startsWith('http') ? site.url : `https://${site.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-primary-600 p-1"
                                title="Visit Platform"
                              >
                                <ExternalLink size={13} />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-gray-200 p-4 text-center dark:border-white/10">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Telemetry testing is currently underway for this script.
                        </p>
                        <Link
                          to="/script-requests"
                          className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-primary-50 px-3 py-1.5 text-xs font-bold text-primary-700 hover:bg-primary-100 dark:bg-primary-950/40 dark:text-primary-300"
                        >
                          <ShieldCheck size={13} /> Request Platform Test
                        </Link>
                      </div>
                    )}

                  </div>
                </InfoSection>
              ) : (
                <InfoSection title={t('product.bundle_tested_sites_title', 'Tested Sites for Included Scripts')} icon={ShieldCheck}>
                  <div className="mt-2 space-y-3">
                    <div className="rounded-xl bg-primary-50/80 p-3 text-xs text-primary-900 dark:bg-primary-950/30 dark:text-primary-200">
                      <strong>Bundle Multi-Tool Coverage:</strong> This bundle combines multiple tools. Below are the verified platforms where individual scripts in this pack have been tested and confirmed working.
                    </div>

                    {productTestedSites.length > 0 ? (
                      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {productTestedSites.map((site) => (
                          <div
                            key={site.id}
                            className="flex items-start justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3 transition-colors hover:border-gray-200 dark:border-white/10 dark:bg-white/[0.035]"
                          >
                            <div className="flex items-start gap-2.5 min-w-0">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200/80 bg-white p-1 shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
                                <img
                                  src={site.iconUrl || getDomainFavicon(site.url)}
                                  alt=""
                                  className="h-full w-full object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                              <div className="min-w-0">
                                <span className="inline-block px-1.5 py-0.5 rounded-md bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300 text-[10px] font-bold mb-1 truncate max-w-full">
                                  Tool: {site.productName || 'Included Script'}
                                </span>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-xs text-gray-950 dark:text-white truncate">
                                    {site.name}
                                  </span>
                                  <span className="inline-flex items-center px-1.5 py-0.2 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                                    Verified
                                  </span>
                                </div>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                  {site.domain}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                              {site.accuracyRate && (
                                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400">
                                  {site.accuracyRate}
                                </span>
                              )}
                              <a
                                href={site.url?.startsWith('http') ? site.url : `https://${site.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-primary-600 p-1"
                                title="Visit Platform"
                              >
                                <ExternalLink size={13} />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-gray-200 p-4 text-center dark:border-white/10">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Telemetry testing is currently underway for scripts included in this bundle.
                        </p>
                        <Link
                          to="/script-requests"
                          className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-primary-50 px-3 py-1.5 text-xs font-bold text-primary-700 hover:bg-primary-100 dark:bg-primary-950/40 dark:text-primary-300"
                        >
                          <ShieldCheck size={13} /> Request Platform Test
                        </Link>
                      </div>
                    )}

                  </div>
                </InfoSection>
              )}

              {availableCoupons.length > 0 && (
                <InfoSection title="Available Offers" icon={Ticket}>
                  <div className="mt-3 flex flex-col gap-3">
                    {availableCoupons.map(coupon => (
                      <CouponCard 
                        key={coupon.code} 
                        coupon={coupon} 
                        productPrice={getPrice()} 
                      />
                    ))}
                  </div>
                </InfoSection>
              )}
            </section>
          </motion.div>
        </div>
      </div>

      {/* RELATED PRODUCTS SECTION */}
      <div className="mx-auto mt-8 max-w-7xl px-3 md:px-6">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h3 className="text-xl font-black tracking-tight text-gray-950 dark:text-white">{t('product.related_title')}</h3>
            <p className="mt-0.5 text-xs font-bold text-gray-500 dark:text-gray-400">{t('product.related_subtitle', 'More picks from this category')}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {(() => {
            const related = lookupProducts
              .filter((p) => p.id !== id && (!product?.category || p.category === product?.category))
              .slice(0, 4);

            return related.map((p, idx) => (
              <div key={p.id || idx} className="h-full">
                <ProductCard product={p} index={idx} viewMode="grid" isCompact={true} />
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Extensions Modal */}

      {showExtensionModal && <ExtensionModal isOpen={!!showExtensionModal} onClose={() => setShowExtensionModal(false)} data={showExtensionModal} />}

      {showAddonSelectionModal && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/55 p-3 backdrop-blur-sm sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            className="w-full max-w-lg overflow-hidden rounded-[1.75rem] border border-white/80 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-950"
          >
            <div className="border-b border-gray-100 bg-gradient-to-br from-white via-violet-50 to-amber-50 px-4 py-4 dark:border-white/10 dark:bg-none">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-violet-700 dark:bg-violet-400/10 dark:text-violet-200">
                    <Sparkles className="h-3.5 w-3.5" />
                    {t('product.addons_available', 'Addons available')}
                  </div>
                  <h3 className="mt-2 text-lg font-black text-gray-950 dark:text-white">
                    {t('product.choose_addons_title', 'Add optional features?')}
                  </h3>
                  <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t('product.choose_addons_subtitle', 'Select extras now, or skip and add them later from this product page.')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddonSelectionModal(false);
                    setSelectedPromptAddonIds([]);
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-300 dark:hover:bg-white/[0.1]"
                  aria-label={t('common.close', 'Close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="max-h-[58vh] space-y-2 overflow-y-auto p-4">
              {addonPromptOptions.map(({ addon, addonContext }) => {
                const isSelected = selectedPromptAddonIds.includes(addon.id);
                return (
                  <button
                    key={addon.id}
                    type="button"
                    onClick={() => setSelectedPromptAddonIds((current) => (
                      current.includes(addon.id)
                        ? current.filter((id) => id !== addon.id)
                        : [...current, addon.id]
                    ))}
                    className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition-all ${isSelected
                      ? 'border-violet-400 bg-violet-50 shadow-sm ring-4 ring-violet-500/10 dark:border-violet-300/50 dark:bg-violet-400/10'
                      : 'border-gray-100 bg-gray-50 hover:border-violet-200 hover:bg-white dark:border-white/10 dark:bg-white/[0.045] dark:hover:bg-white/[0.07]'
                    }`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-violet-100 text-violet-700 dark:bg-violet-400/10 dark:text-violet-200">
                      {addon.image ? (
                        <img src={addon.image} alt={addon.name} className="h-full w-full object-cover" />
                      ) : (
                        <Sparkles className="h-5 w-5" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-black text-gray-950 dark:text-white notranslate" translate="no">
                        {addon.name}
                      </span>
                      {addon.description && (
                        <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                          {addon.description}
                        </span>
                      )}
                      <span className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-black text-primary-600 dark:text-primary-300 notranslate" translate="no">
                          {addonContext.price > 0 ? formatPrice(addonContext.price) : t('product.free', 'Free')}
                        </span>
                        {addonContext.validity && (
                          <span className="font-bold text-gray-500 dark:text-gray-400">
                            /{formatDurationLabel(addonContext.validity)}
                          </span>
                        )}
                        {addonContext.isAdjusted && (
                          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-violet-700 dark:bg-gray-950/70 dark:text-violet-200">
                            {t('product.adjusted_price', 'Adjusted')}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${isSelected
                      ? 'border-violet-600 bg-violet-600 text-white'
                      : 'border-gray-300 bg-white text-transparent dark:border-white/20 dark:bg-white/[0.06]'
                    }`}>
                      <Check className="h-3.5 w-3.5" />
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-2 border-t border-gray-100 p-4 dark:border-white/10">
              <button
                type="button"
                onClick={() => addProductAndSelectedAddonsToCart([])}
                className="h-12 rounded-2xl border border-gray-200 bg-white text-sm font-black text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-200 dark:hover:bg-white/[0.1]"
              >
                {t('product.skip_addons', 'Skip addons')}
              </button>
              <button
                type="button"
                onClick={() => addProductAndSelectedAddonsToCart(selectedPromptAddonIds)}
                className="h-12 rounded-2xl bg-primary-600 text-sm font-black text-white shadow-lg shadow-primary-600/15 transition-all hover:-translate-y-0.5 hover:bg-primary-700 dark:bg-white dark:text-gray-950 dark:shadow-none dark:hover:bg-gray-100"
              >
                {selectedPromptAddonIds.length > 0
                  ? t('product.add_selected_addons', { count: selectedPromptAddonIds.length, defaultValue: `Add ${selectedPromptAddonIds.length} selected` })
                  : t('product.add_without_addons', 'Add product')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Mobile Sticky Footer */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200/70 bg-white/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-18px_40px_-30px_rgba(15,23,42,0.8)] backdrop-blur-xl dark:border-white/10 dark:bg-gray-950/95 md:hidden">
        <div className="flex items-center gap-3">
          {isPurchasable && !isExternal && !isFreeScript && (
            <div className="min-w-[96px]">
              <span className="block text-[10px] font-black uppercase tracking-wider text-gray-500">{t('product.total_price')}</span>
              <span className="block truncate text-lg font-black text-gray-950 dark:text-white notranslate" translate="no">
                {formatPrice(isDiscounted ? finalPrice : currentBasePrice)}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            {renderPrimaryAction({ compact: true })}
          </div>
        </div>
      </div>
      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        productName={displayName}
        shareLink={generateShareLink()}
      />

    </div >
  );
};

export default ProductDetail;
